import type { AppLoadingStage } from '@/lib/loadingMessages';
import * as Sentry from '@sentry/react-native';
import { useEffect, useRef } from 'react';
import { Platform } from 'react-native';

/** Fraction of sessions that report the >5s warning (8.6k events/90d at 100%). */
const SLOW_LOADING_SAMPLE_RATE = 0.1;

/**
 * Tracks loading duration and sends Sentry diagnostics:
 * - Warning at 5s if still loading (stage + context), sampled at
 *   SLOW_LOADING_SAMPLE_RATE. Multiply counts by 1/rate for true incidence.
 * - Breadcrumb (no event) when loading resolves, with total duration.
 */
export function useLoadingTracker({
  isLoading,
  stage,
  hasSsoToken,
  resolvedAsError,
}: {
  isLoading: boolean;
  stage: AppLoadingStage | null;
  hasSsoToken: boolean;
  resolvedAsError: boolean;
}) {
  const startRef = useRef<number>(Date.now());
  const warnedRef = useRef(false);
  const resolvedRef = useRef(false);
  const sampledRef = useRef(Math.random() < SLOW_LOADING_SAMPLE_RATE);

  useEffect(() => {
    if (!isLoading) {
      if (warnedRef.current && !resolvedRef.current) {
        resolvedRef.current = true;
        const totalMs = Date.now() - startRef.current;
        try {
          Sentry.addBreadcrumb({
            category: 'app.loading',
            level: 'info',
            message: '[AppLoading] Loading resolved after slow start',
            data: { totalDurationMs: totalMs, resolvedAs: resolvedAsError ? 'error' : 'success', platform: Platform.OS },
          });
        } catch {}
      }
      startRef.current = Date.now();
      warnedRef.current = false;
      resolvedRef.current = false;
      return;
    }

    const elapsed = Date.now() - startRef.current;
    const remaining = Math.max(0, 5000 - elapsed);
    const timer = setTimeout(() => {
      if (!warnedRef.current) {
        warnedRef.current = true;
        if (!sampledRef.current) return;
        try {
          Sentry.captureMessage('[AppLoading] UI stuck in loading for >5s', {
            level: 'warning',
            extra: {
              stage,
              durationMs: Date.now() - startRef.current,
              platform: Platform.OS,
              hasSsoToken,
              sampleRate: SLOW_LOADING_SAMPLE_RATE,
            },
          });
        } catch {}
      }
    }, remaining);
    return () => clearTimeout(timer);
  }, [isLoading, stage, hasSsoToken, resolvedAsError]);
}
